import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'

import type { LotoPageDetails, LotoPageTarget } from './lotoPageLookups'
import { mergeAttachments } from './lotoReportDraft'
import type { LotoAttachment } from './lotoReportSchemas'

type LotoAttachedPage = {
  pageId: string
  target: LotoPageTarget | undefined
}

export type LotoAttachmentGroup = {
  systemDocumentId: string
  title: string | undefined
  number: string | undefined
  document: DocumentTableRow | undefined
  pages: LotoAttachedPage[]
}

export type LotoPageReference = {
  documentName: string | undefined
  target: LotoPageTarget | undefined
}

const titleCollator = new Intl.Collator(undefined, { numeric: true })

function comparePages(a: LotoAttachedPage, b: LotoAttachedPage): number {
  if (a.target && b.target) return a.target.pageNumber - b.target.pageNumber
  if (a.target) return -1
  if (b.target) return 1
  return 0
}

function compareGroups(a: LotoAttachmentGroup, b: LotoAttachmentGroup): number {
  if (a.title !== undefined && b.title !== undefined)
    return titleCollator.compare(a.title, b.title)
  if (a.title !== undefined) return -1
  if (b.title !== undefined) return 1
  return 0
}

export function groupAttachedPages(
  attachments: LotoAttachment[],
  details: LotoPageDetails | undefined
): LotoAttachmentGroup[] {
  return mergeAttachments([], attachments)
    .map(({ system_document_id, page_ids }) => ({
      systemDocumentId: system_document_id,
      title: details?.systemDocumentIdToTitle.get(system_document_id),
      number: details?.systemDocumentIdToNumber.get(system_document_id),
      document: details?.systemDocumentIdToDocument.get(system_document_id),
      pages: page_ids
        .map((pageId) => ({
          pageId,
          target: details?.pageIdToTarget.get(pageId),
        }))
        .sort(comparePages),
    }))
    .sort(compareGroups)
}

export function lotoPageIdToReference(
  groups: LotoAttachmentGroup[]
): ReadonlyMap<string, LotoPageReference> {
  return new Map(
    groups.flatMap(({ number, title, pages }) =>
      pages.map(({ pageId, target }) => [
        pageId,
        { documentName: number ?? title, target },
      ])
    )
  )
}
